/**
 * Short activation form — Sidebar `footer` slot host (DC-15, AC-031/053/054).
 * Also the Popover body for the `badge` variant (`ActivationBadgeBody`).
 * Router-free: the projection and `onNavigate` are props; dialogs (class D)
 * are hosted by the parent ActivationChecklist and reached through `openDialog`.
 */
import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { ActivationAction, ActivationProjection } from '@modules/shared-types';
import { runActivationAction } from '../actions/runActivationAction';
import type { ActivationActionContext } from '../actions/runActivationAction';
import { LAUNCH_ACTIVATION_TEST_IDS } from '../testIds';
import { SHORT_FORM_COPY } from '../copy/shortFormCopy';
import { actionVerb, describeActionLanding, fullListPath } from '../utils/shortFormPresenter';
import { CHIP_FOCUS_SX } from './chipFocusSx';

export interface ActivationBodyProps {
  projection: ActivationProjection;
  onNavigate: (path: string) => void;
  openDialog?: (dialog: NonNullable<ActivationAction['dialog']>, props?: Record<string, string>) => boolean | void;
  /** Sidebar collapsed → render only the chip. */
  collapsed?: boolean;
  onRefresh: () => void;
  questionnaireUrl?: string;
}

/** Alias — Task 2 interfaces (brief §Interfaces) referred to this name; same shape as `ActivationBodyProps`. */
export type ActivationShortFormProps = ActivationBodyProps;

export const ActivationShortForm: React.FC<ActivationBodyProps> = ({ projection, onNavigate, openDialog, collapsed = false }) => {
  const { projectId, earnedCount, totalCount, complete, next } = projection;
  const percent = totalCount === 0 ? 0 : Math.round((earnedCount / totalCount) * 100);
  const openFullList = (): void => onNavigate(fullListPath(projectId));

  if (collapsed) {
    const chipLabel = complete ? SHORT_FORM_COPY.chipComplete : SHORT_FORM_COPY.chip(earnedCount, totalCount);
    const tooltip = complete ? SHORT_FORM_COPY.complete : `${SHORT_FORM_COPY.title}: ${SHORT_FORM_COPY.count(earnedCount, totalCount)}`;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 0.5 }}>
        <Tooltip title={tooltip} placement="right" arrow>
          <Chip
            size="small"
            variant="outlined"
            color={complete ? 'success' : 'warning'}
            label={chipLabel}
            onClick={openFullList}
            data-testid={LAUNCH_ACTIVATION_TEST_IDS.chip}
            sx={{ '& .MuiChip-label': { px: 0.75 }, ...CHIP_FOCUS_SX }}
          />
        </Tooltip>
      </Box>
    );
  }

  const action = next?.action;
  const handleAction = (): void => {
    if (action) {
      runActivationAction(action, { onNavigate, openDialog: openDialog as ActivationActionContext['openDialog'] });
    }
  };

  return (
    <Box
      data-testid={LAUNCH_ACTIVATION_TEST_IDS.shortForm}
      sx={{ m: 1, p: 1.5, pb: 1.25, border: 1, borderColor: 'divider', borderRadius: 3, minWidth: 0 }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{SHORT_FORM_COPY.title}</Typography>
        <Typography variant="caption" sx={{ color: complete ? 'success.main' : 'text.secondary' }}>
          {SHORT_FORM_COPY.count(earnedCount, totalCount)}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percent}
        color={complete ? 'success' : 'primary'}
        sx={{ height: 5, borderRadius: 3, mb: 1.25 }}
      />
      {complete || !next ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <CheckCircleIcon color="success" fontSize="small" />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{SHORT_FORM_COPY.complete}</Typography>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1.25 }} data-testid={LAUNCH_ACTIVATION_TEST_IDS.shortFormNext}>
          <WarningAmberIcon color="warning" fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{next.title}</Typography>
            {action && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', overflowWrap: 'anywhere' }}>
                {describeActionLanding(action)}
              </Typography>
            )}
          </Box>
        </Stack>
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Link component="button" variant="caption" onClick={openFullList} data-testid={LAUNCH_ACTIVATION_TEST_IDS.shortFormAll}>
          {SHORT_FORM_COPY.allSteps}
        </Link>
        {!complete && action && (
          <Button size="small" variant="contained" onClick={handleAction} sx={{ flexShrink: 0 }} data-testid={LAUNCH_ACTIVATION_TEST_IDS.shortFormAction}>
            {actionVerb(action)}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
